import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';

// CSS для анимации тултипа
const tooltipAnimationStyle = `
  @keyframes slideUpFromBottom {
    0% {
      opacity: 0;
      transform: translateY(100%);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes slideDownToBottom {
    0% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(100%);
    }
  }
`;

// Функция для расчета времени до сброса лидерборда (аналогично попыткам)
const calculateTimeUntilLeaderboardReset = () => {
    const now = new Date();
    const tomorrow = new Date(now.getTime());
    // Устанавливаем следующую полночь по UTC
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    tomorrow.setUTCHours(0, 0, 0, 0); // Полночь следующего дня по UTC

    const diff = tomorrow.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

interface WalletStats {
  id: number;
  wallet_address: string;
  discord_username?: string;
  wins: number;
  draws: number;
  losses: number;
  max_win_streak: number;
  current_win_streak: number;
}

interface Top10HistoryStats {
  id: number;
  wallet_address: string;
  wins: number;
  draws: number;
  losses: number;
  max_win_streak: number;
  daily_games_count: number;
  last_game_date: string;
  reset_date: string;
  position: number;
  display_name: string;
  discord_username?: string;
}

interface LeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LeaderboardModal({ isOpen, onClose }: LeaderboardModalProps) {
  const [stats, setStats] = useState<WalletStats[]>([]);
  const [allStats, setAllStats] = useState<WalletStats[]>([]); // Все кошельки для расчета ранга
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [userStat, setUserStat] = useState<WalletStats | null>(null);
  const [searchResults, setSearchResults] = useState<WalletStats[]>([]);
  const [timeUntilLeaderboardReset, setTimeUntilLeaderboardReset] = useState('');
  const [top10History, setTop10History] = useState<Top10HistoryStats[]>([]);
  const [showTop10Mode, setShowTop10Mode] = useState(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);
  const [isSwitchingMode, setIsSwitchingMode] = useState<boolean>(false);
  const [isSwitchButtonAnimated, setIsSwitchButtonAnimated] = useState<boolean>(false);
  const [tableAnimationPhase, setTableAnimationPhase] = useState<'idle' | 'fade-out' | 'fade-in' | 'slide-left' | 'slide-right' | 'slide-center' | 'scale-out' | 'scale-in'>('idle');

  const { publicKey } = useWallet();

  const normalizeStat = (raw: any): WalletStats => {
    return {
      id: typeof raw?.id === 'number' ? raw.id : 0,
      wallet_address: raw?.wallet_address ?? raw?.walletAddress ?? '',
      discord_username: raw?.discord_username || undefined,
      wins: Number(raw?.wins ?? 0),
      draws: Number(raw?.draws ?? 0),
      losses: Number(raw?.losses ?? 0),
      max_win_streak: Number(raw?.max_win_streak ?? 0),
      current_win_streak: Number(raw?.current_win_streak ?? 0),
    } as WalletStats;
  };

  const formatWalletAddress = (address: string) => {
    if (address.length <= 10) return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const getDisplayName = (stat: WalletStats | Top10HistoryStats) => {
    return stat.discord_username || formatWalletAddress(stat.wallet_address);
  };

  const copyToClipboard = (address: string) => {
    navigator.clipboard.writeText(address)
      .then(() => {
        setCopiedAddress(address);
        setTimeout(() => setCopiedAddress(null), 2000);
      })
      .catch(err => console.error('Failed to copy address to clipboard:', err));
  };


  const searchWallet = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(`/api/search-wallet/${encodeURIComponent(query)}`);
      if (response.ok) {
        const results = await response.json();
        const normalizedResults = Array.isArray(results) ? results.map(normalizeStat) : [];
        setSearchResults(normalizedResults);
        console.log('Search results:', normalizedResults.length, 'wallets found');
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    }
  };

  // Блокировка прокрутки страницы при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        // Fetch top-100 leaderboard, all wallets for rank calculation, user stats, and top5 history in parallel
        const leaderboardPromise = (async () => {
          const response = await fetch('/api/leaderboard');
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Leaderboard fetch error:', response.status, response.statusText, errorText);
            throw new Error(`Failed to fetch leaderboard data: ${response.status}`);
          }
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            throw new Error('Invalid server response format');
          }
          return (await response.json()) as WalletStats[];
        })();

        const allLeaderboardPromise = (async () => {
          const response = await fetch('/api/leaderboard-all');
          if (!response.ok) {
            console.warn('All leaderboard fetch error:', response.status, response.statusText);
            return [];
          }
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            return [];
          }
          return (await response.json()) as WalletStats[];
        })();

        const userStatsPromise = (async () => {
          if (!publicKey) return null;
          const addr = publicKey.toString();
          try {
            const url = '/api/wallet-stats/' + encodeURIComponent(addr);
            const res = await fetch(url);
            if (res.status === 404) return null; // user not in DB -> show default leaderboard
            if (!res.ok) {
              const errorText = await res.text();
              console.warn('User stats fetch error:', res.status, res.statusText, errorText);
              return null; // Do not block leaderboard rendering
            }
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) return null;
            return (await res.json()) as WalletStats;
          } catch (e) {
            console.warn('User stats fetch failed:', e);
            return null;
          }
        })();

        const top10HistoryPromise = (async () => {
          try {
            const response = await fetch('/api/leaderboard-top10-history');
            if (!response.ok) {
              console.warn('Top10 history fetch error:', response.status, response.statusText);
              return [];
            }
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              return [];
            }
            return (await response.json()) as Top10HistoryStats[];
          } catch (e) {
            console.warn('Top10 history fetch failed:', e);
            return [];
          }
        })();

        const [leaderboard, allLeaderboard, user, top10HistoryData] = await Promise.all([
          leaderboardPromise,
          allLeaderboardPromise,
          userStatsPromise,
          top10HistoryPromise
        ]);

        const normalizedLeaderboard = Array.isArray(leaderboard)
          ? leaderboard.map(normalizeStat)
          : [];
        const normalizedAllLeaderboard = Array.isArray(allLeaderboard)
          ? allLeaderboard.map(normalizeStat)
          : [];

        console.log('Leaderboard data received:', normalizedLeaderboard.length, 'records');
        console.log('All leaderboard data received:', normalizedAllLeaderboard.length, 'records');
        console.log('Top10 history data received:', top10HistoryData.length, 'records');

        setStats(normalizedLeaderboard);
        setAllStats(normalizedAllLeaderboard);
        setUserStat(user ? normalizeStat(user) : null);
        setTop10History(top10HistoryData);
      } catch (err) {
        console.error('Leaderboard fetch error details:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [isOpen, publicKey]);

  // Таймер до сброса лидерборда
  useEffect(() => {
    if (!isOpen) return;

    // Устанавливаем начальное значение таймера
    setTimeUntilLeaderboardReset(calculateTimeUntilLeaderboardReset());

    const intervalId = setInterval(() => {
      setTimeUntilLeaderboardReset(calculateTimeUntilLeaderboardReset());
    }, 1000); // Обновляем каждую секунду

    return () => clearInterval(intervalId);
  }, [isOpen]);

  // Поиск с задержкой
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchWallet(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);



  // Управление анимациями открытия/закрытия
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animation
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Определяем какие данные показывать: результаты поиска, топ-10 или основной лидерборд
  const baseStats = showTop10Mode ? top10History : stats;
  const filteredStats = searchQuery
    ? (searchResults.length > 0
        ? searchResults
        :       baseStats.filter(stat =>
        stat.wallet_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (stat.discord_username && stat.discord_username.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    )
    : baseStats;

  // If the user is present in the leaderboard, avoid duplicating them in the list below
  const displayedStats = userStat && !searchQuery
    ? filteredStats.filter(s => s.wallet_address !== userStat.wallet_address)
    : filteredStats;

  // Используем allStats для правильного расчета ранга из ВСЕХ кошельков
  const sortedStatsForRank = allStats.length > 0
    ? [...allStats].sort((a, b) => {
        // 1. Количество побед (DESC)
        if (a.wins !== b.wins) return b.wins - a.wins;

        // 2. Количество сыгранных игр (DESC)
        const aTotalGames = a.wins + a.draws + a.losses;
        const bTotalGames = b.wins + b.draws + b.losses;
        if (aTotalGames !== bTotalGames) return bTotalGames - aTotalGames;

        // 3. Максимальный win streak (DESC)
        if (a.max_win_streak !== b.max_win_streak) return b.max_win_streak - a.max_win_streak;

        // 4. ID для детерминированной сортировки
        return a.id - b.id;
      })
    : [...stats].sort((a, b) => {
        // 1. Количество побед (DESC)
        if (a.wins !== b.wins) return b.wins - a.wins;

        // 2. Количество сыгранных игр (DESC)
        const aTotalGames = a.wins + a.draws + a.losses;
        const bTotalGames = b.wins + b.draws + b.losses;
        if (aTotalGames !== bTotalGames) return bTotalGames - aTotalGames;

        // 3. Максимальный win streak (DESC)
        if (a.max_win_streak !== b.max_win_streak) return b.max_win_streak - a.max_win_streak;

        // 4. ID для детерминированной сортировки
        return a.id - b.id;
      });

  // Функция для расчета ранга любого кошелька
  const calculateRank = (walletAddress: string): number => {
    if (showTop10Mode) {
      // Для топ-10 режима используем поле position из данных
      const top10Stat = top10History.find(s => s.wallet_address === walletAddress);
      return top10Stat ? top10Stat.position : 0;
    }
    return sortedStatsForRank.findIndex(s => s.wallet_address === walletAddress) + 1;
  };

  if (!shouldRender) return null;

  const ROW_HEIGHT = 53;
  const HEADER_HEIGHT = 43;
  const VISIBLE_ROWS = showTop10Mode ? 10 : 10;
  const TABLE_HEIGHT = ROW_HEIGHT * VISIBLE_ROWS + HEADER_HEIGHT;

  return (
    <>
      <style>{`
        ${tooltipAnimationStyle}
        .modal-content {
          opacity: 0;
          transform: scale(0.5);
          transition: all 0.15s ease-out;
        }

        .modal-content.entering {
          opacity: 1;
          transform: scale(1);
        }

        .modal-content.exiting {
          opacity: 0;
          transform: scale(0.5);
        }

        .table-transition {
          transition: all 0.3s ease-out;
        }

        .table-transition.fade-out {
          opacity: 0;
          transform: translateX(-20px);
        }

        .table-transition.fade-in {
          opacity: 1;
          transform: translateX(0);
        }

        .table-transition.slide-left {
          opacity: 0;
          transform: translateX(-100%);
        }

        .table-transition.slide-right {
          opacity: 0;
          transform: translateX(100%);
        }

        .table-transition.slide-center {
          opacity: 1;
          transform: translateX(0);
        }

        .table-transition.scale-out {
          opacity: 0;
          transform: scale(0.95);
        }

        .table-transition.scale-in {
          opacity: 1;
          transform: scale(1);
        }

        @keyframes shimmer {
          0% {
            background-position: -200px 0;
          }
          100% {
            background-position: calc(200px + 100%) 0;
          }
        }

        .shimmer-effect {
          position: relative;
          overflow: hidden;
        }

        .shimmer-effect::after {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          transform: translateX(-100%);
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          animation: shimmer 1.5s infinite;
          content: '';
        }

        .switching-animation {
          animation: pulse 0.6s ease-in-out;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div
            className={`relative bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-3 sm:p-5 md:p-6 max-w-4xl w-full mx-2 sm:mx-4 overflow-hidden flex flex-col animate-border-glow ${
              showTop10Mode ? 'max-h-[80vh] sm:max-h-[90vh] md:max-h-[95vh]' : 'max-h-[80vh] sm:max-h-[90vh] md:max-h-[95vh]'
            } modal-content ${
              isAnimating ? 'entering' : 'exiting'
            }`}
        >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 text-purple-400 hover:text-white transition-colors z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="flex flex-col gap-1 mb-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl sm:text-2xl font-bold text-white gradient-text">{showTop10Mode ? 'Top 10 Leaderboard' : 'Leaderboard'}</h2>
          </div>
        </div>
        <p className="text-xs sm:text-sm text-purple-400 mb-3">
          Resets in: {timeUntilLeaderboardReset}
        </p>
        {/* <div className="glass-card bg-black/60 border border-purple-800/50 rounded-lg px-3 py-2 w-full mb-4">
          <p className="text-xs sm:text-sm text-purple-300">
            ❓ Top-10 is calculated based on the number of wins, games played, and maximum number of wins in a row. Updates every day at 00:00 UTC
          </p>
        </div> */}

        <div className="relative mb-4 flex items-center">
          <div className="absolute z-10 pointer-events-none" style={{ paddingLeft: "0.7rem" }}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder={showTop10Mode ? "Search by wallet or Discord" : "Search by wallet or Discord"}
            style={{ paddingLeft: "2.5rem", backgroundColor: "black" }}
            autoComplete="off"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={showTop10Mode}
            className={`w-full rounded-lg py-2 pl-14 pr-4 ${
              showTop10Mode
                ? '!bg-black border border-gray-600/50 text-gray-500 placeholder-gray-500/50 cursor-not-allowed'
                : '!bg-black border border-purple-800/50 text-purple-200 placeholder-purple-400/70 focus:outline-none focus:ring-2 focus:ring-purple-500/50'
            }`}
          />
          {searchQuery && !showTop10Mode && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 z-10 text-purple-400 hover:text-purple-300"
              aria-label="Clear search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        <div className="overflow-hidden">
          {loading || isSwitchingMode ? (
            <div className="flex justify-center items-center h-40">
              <p className="text-purple-400">{isSwitchingMode ? "Switching view..." : "Loading stats..."}</p>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-40">
              <p className="text-red-500">Error: {error}</p>
            </div>
          ) : filteredStats.length === 0 ? (
            <div className="flex justify-center items-center h-40">
              <p className="text-purple-400">
                {stats.length === 0 ? "No data" : "No wallets matching your search"}
              </p>
            </div>
          ) : (
            <div
              className={`overflow-y-auto table-transition ${
                tableAnimationPhase === 'fade-out' ? 'fade-out' :
                tableAnimationPhase === 'fade-in' ? 'fade-in' :
                isSwitchingMode ? 'shimmer-effect switching-animation' : ''
              }`}
              style={{ height: `${TABLE_HEIGHT}px` }}
            >
              <table className="w-full text-sm text-left text-purple-300">
                <thead className="text-xs uppercase bg-black/60 text-purple-300 border-b border-purple-800/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 sm:px-6 py-3">Rank</th>
                    <th className="px-3 sm:px-6 py-3">Wallet / Discord Username</th>
                    <th className="px-3 sm:px-6 py-3 text-center">Wins</th>
                    <th className="px-3 sm:px-6 py-3 text-center">Draws</th>
                    <th className="px-3 sm:px-6 py-3 text-center">Losses</th>
                    <th className="px-3 sm:px-6 py-3 text-center">Win Streak</th>
                    <th className="px-3 sm:px-6 py-3 text-center">Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Pinned current user row (only when connected and found in DB, and no active search filter) */}
                  {userStat && !searchQuery && (!showTop10Mode || (showTop10Mode && top10History.some(stat => stat.wallet_address === userStat.wallet_address))) && (
                    (() => {
                      const totalGames = userStat.wins + userStat.draws + userStat.losses;
                      const winRate = totalGames > 0 ? Math.round((userStat.wins / totalGames) * 100) : 0;
                      // Рассчитываем ранг пользователя из полного списка
                      const userRank = calculateRank(userStat.wallet_address);

                      return (
                        <tr key={`you-${userStat.id}`} className="bg-purple-900/40">
                          <td className="px-3 sm:px-6 py-4 font-medium">
                            {userRank}
                          </td>
                          <td className="px-3 sm:px-6 py-4 font-mono text-purple-200">
                            <div className="flex items-center gap-2">
                              <span className="text-white">You</span>
                              <span className="opacity-70">
                                ({userStat.discord_username ? `@${userStat.discord_username}` : formatWalletAddress(userStat.wallet_address)})
                              </span>
                              <button
                                onClick={() => copyToClipboard(userStat.wallet_address)}
                                className="text-purple-400 hover:text-purple-300 transition-colors"
                                aria-label="Copy wallet address"
                                title="Copy wallet address"
                              >
                                {copiedAddress === userStat.wallet_address ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-4 text-green-400 text-center">{userStat.wins}</td>
                          <td className="px-3 sm:px-6 py-4 text-yellow-400 text-center">{userStat.draws}</td>
                          <td className="px-3 sm:px-6 py-4 text-red-400 text-center">{userStat.losses}</td>
                          <td className="px-3 sm:px-6 py-4 text-orange-400 text-center">{userStat.max_win_streak}</td>
                          <td className="px-3 sm:px-6 py-4 text-center">
                            <div className="flex items-center justify-center">
                              <div className="relative w-8 h-2.5 bg-purple-900/30 rounded-full overflow-hidden mr-2">
                                <div
                                  className="absolute top-0 left-0 h-full rounded-full"
                                  style={{
                                    width: `${Math.max(3, winRate)}%`,
                                    backgroundColor: winRate > 70 ? '#a855f7' : winRate > 40 ? '#7e22ce' : '#581c87'
                                  }}
                                />
                              </div>
                              <span>{winRate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })()
                  )}

                  {displayedStats
                    .sort((a, b) => {
                      // 1. Количество побед (DESC)
                      if (a.wins !== b.wins) return b.wins - a.wins;

                      // 2. Количество сыгранных игр (DESC)
                      const aTotalGames = a.wins + a.draws + a.losses;
                      const bTotalGames = b.wins + b.draws + b.losses;
                      if (aTotalGames !== bTotalGames) return bTotalGames - aTotalGames;

                      // 3. Максимальный win streak (DESC)
                      if (a.max_win_streak !== b.max_win_streak) return b.max_win_streak - a.max_win_streak;

                      // 4. ID для детерминированной сортировки
                      return a.id - b.id;
                    })
                    .map((stat, index) => {
                      const totalGames = stat.wins + stat.draws + stat.losses;
                      const winRate = totalGames > 0 
                        ? Math.round((stat.wins / totalGames) * 100) 
                        : 0;
                      
                      // Логика отображения ранга в зависимости от режима
                      let displayRank: number;
                      if (showTop10Mode) {
                        // В режиме топ-10 используем сохраненную позицию
                        displayRank = 'position' in stat ? (stat as Top10HistoryStats).position : calculateRank(stat.wallet_address);
                      } else if (searchQuery) {
                        // Для поиска показываем реальный ранг
                        displayRank = calculateRank(stat.wallet_address);
                      } else {
                        // Для обычного лидерборда показываем позицию в отсортированном списке
                        displayRank = [...stats].sort((a, b) => {
                            // 1. Количество побед (DESC)
                            if (a.wins !== b.wins) return b.wins - a.wins;

                            // 2. Количество сыгранных игр (DESC)
                            const aTotalGames = a.wins + a.draws + a.losses;
                            const bTotalGames = b.wins + b.draws + b.losses;
                            if (aTotalGames !== bTotalGames) return bTotalGames - aTotalGames;

                            // 3. Максимальный win streak (DESC)
                            if (a.max_win_streak !== b.max_win_streak) return b.max_win_streak - a.max_win_streak;

                            // 4. ID для детерминированной сортировки
                            return a.id - b.id;
                          }).findIndex(s => s.id === stat.id) + 1;
                      }
                      
                      return (
                        <tr key={stat.id} className={index % 2 === 0 ? 'bg-black/40' : 'bg-purple-900/20'}>
                          <td className="px-3 sm:px-6 py-4 font-medium">
                            {displayRank}
                          </td>
                          <td className="px-3 sm:px-6 py-4 font-mono text-purple-200">
                            <div className="flex items-center gap-2">
                              {searchQuery ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: getDisplayName(stat).replace(
                                      new RegExp(searchQuery, 'gi'),
                                      match => `<span class="bg-purple-500/30 text-purple-100">${match}</span>`
                                    )
                                  }}
                                />
                              ) : (
                                <span>
                                  {stat.discord_username ? (
                                    <span className="text-purple-300">@{stat.discord_username}</span>
                                  ) : (
                                    formatWalletAddress(stat.wallet_address)
                                  )}
                                </span>
                              )}
                              <button
                                onClick={() => copyToClipboard(stat.wallet_address)}
                                className="text-purple-400 hover:text-purple-300 transition-colors"
                                aria-label="Copy wallet address"
                                title="Copy wallet address"
                              >
                                {copiedAddress === stat.wallet_address ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-4 text-green-400 text-center">{stat.wins}</td>
                          <td className="px-3 sm:px-6 py-4 text-yellow-400 text-center">{stat.draws}</td>
                          <td className="px-3 sm:px-6 py-4 text-red-400 text-center">{stat.losses}</td>
                          <td className="px-3 sm:px-6 py-4 text-orange-400 text-center">{stat.max_win_streak}</td>
                          <td className="px-3 sm:px-6 py-4 text-center">
                            <div className="flex items-center justify-center">
                              <div className="relative w-8 h-2.5 bg-purple-900/30 rounded-full overflow-hidden mr-2">
                                <div
                                  className="absolute top-0 left-0 h-full rounded-full"
                                  style={{
                                    width: `${Math.max(3, winRate)}%`,
                                    backgroundColor: winRate > 70 ? '#a855f7' : winRate > 40 ? '#7e22ce' : '#581c87'
                                  }}
                                />
                              </div>
                              <span>{winRate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Кнопка переключения между Leaderboard и Top 10 */}
        <div className="relative mt-4">
          <button
            onClick={showTop10Mode ? async () => {
              // Начинаем анимацию выхода
              setTableAnimationPhase('fade-out');
              setIsSwitchingMode(true);

              // Ждем завершения анимации выхода
              await new Promise(resolve => setTimeout(resolve, 300));

              // Меняем режим
              const newMode = !showTop10Mode;
              setShowTop10Mode(newMode);

              // Очищаем поиск при переключении в топ-10
              if (newMode) {
                setSearchQuery('');
              }

              // Начинаем анимацию входа
              setTableAnimationPhase('fade-in');

              // Ждем завершения анимации входа
              await new Promise(resolve => setTimeout(resolve, 300));

              // Завершаем анимацию
              setTableAnimationPhase('idle');
              setIsSwitchingMode(false);
            } : undefined}
            onMouseEnter={() => setIsSwitchButtonAnimated(true)}
            onMouseLeave={() => setIsSwitchButtonAnimated(false)}
            disabled={!showTop10Mode}
            className={`w-full h-12 rounded-lg overflow-visible transition-all duration-300 relative flex items-center justify-center text-white font-bold text-sm ${
              isSwitchingMode ? 'switching-animation' : ''
            } ${!showTop10Mode ? 'cursor-not-allowed' : ''}`}
            title={showTop10Mode ? "Show Leaderboard" : "Under Maintenance"}
            style={{
              background: isSwitchButtonAnimated
                  ? `linear-gradient(135deg,
                      #7C3AED 0%,
                      #8B5CF6 25%,
                      #7C3AED 50%,
                      #8B5CF6 75%,
                      #7C3AED 100%
                    ),
                    repeating-linear-gradient(
                      45deg,
                      rgba(255, 255, 255, 0.4) 0px,
                      rgba(255, 255, 255, 0.4) 2px,
                      rgba(0, 0, 0, 0.1) 2px,
                      rgba(0, 0, 0, 0.1) 12px
                    )`
                  : `linear-gradient(135deg,
                      #8B5CF6 0%,
                      #7C3AED 25%,
                      #8B5CF6 50%,
                      #7C3AED 75%,
                      #8B5CF6 100%
                    ),
                    repeating-linear-gradient(
                      45deg,
                      rgba(255, 215, 0, 0.3) 0px,
                      rgba(255, 215, 0, 0.3) 2px,
                      rgba(255, 215, 0, 0.1) 2px,
                      rgba(255, 215, 0, 0.1) 12px
                    )`,
              backgroundSize: isSwitchButtonAnimated ? '16px 16px' : '20px 20px',
              backgroundPosition: '0 0',
              boxShadow: isSwitchButtonAnimated
                  ? `0 4px 15px rgba(139, 92, 246, 0.4),
                     inset 0 1px 0 rgba(255, 255, 255, 0.3),
                     inset 0 -1px 0 rgba(0, 0, 0, 0.1)`
                  : `0 4px 15px rgba(139, 92, 246, 0.3),
                     inset 0 1px 0 rgba(255, 255, 255, 0.2),
                     inset 0 -1px 0 rgba(0, 0, 0, 0.1)`,
              border: '2px solid rgba(139, 92, 246, 0.5)',
              transition: 'background 0.3s ease-out, background-size 0.3s ease-out, box-shadow 0.3s ease-out'
            }}
          >
            {/* Subtle lighting effect */}
            <div
              className="absolute inset-0 rounded-lg opacity-30"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%, rgba(0, 0, 0, 0.1) 100%)',
              }}
            />
            <span className="relative z-10" style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}>
              {isSwitchingMode ? 'Switching...' : (showTop10Mode ? 'Show Leaderboard' : 'Under Maintenance')}
            </span>
          </button>
        </div>

      </div>
    </div>
    </>
  );
}

export function LeaderboardButton() {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 sm:gap-2.5 bg-purple-700 hover:bg-purple-600 transition-colors rounded-lg px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 text-white shadow-purple-500/30 text-xs sm:text-sm md:text-base"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span>Leaderboard</span>
      </button>
      <LeaderboardModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
} 